import React, { Fragment, useMemo } from 'react';
import useIntls from '@opm/hooks/useIntls';
import type { ICategoryGroupOption, ICategoryGroupValue } from './index';

interface ICategoryGroupSelectButtonProps {
  value: ICategoryGroupValue[];
  options: ICategoryGroupOption[];
  title: string;
  titleCn: string;
  /** 按钮上最多平铺展示几个具体的 L2 名称，超过则展示数量 */
  maxShow?: number;
}

export function CategoryGroupSelectButton({
  value,
  options,
  title,
  titleCn,
  maxShow = 2,
}: ICategoryGroupSelectButtonProps) {
  const { intl, isEn } = useIntls();

  const { selectedTotal, allTotal, selectedTexts, isAll } = useMemo(() => {
    const all = options.reduce(
      (sum, opt) => sum + (opt.children?.length || 0),
      0,
    );
    const childMap: Record<string, { l1: ICategoryGroupOption; l2Map: Record<string, { text: string; textCn: string }> }> = {};
    options.forEach((opt) => {
      const l2Map: Record<string, { text: string; textCn: string }> = {};
      (opt.children || []).forEach((c) => {
        l2Map[c.value] = { text: c.text, textCn: c.textCn };
      });
      childMap[opt.value] = { l1: opt, l2Map };
    });

    const texts: string[] = [];
    let count = 0;
    (value || []).forEach((it) => {
      const group = childMap[it.l1];
      if (!group) return;
      (it.l2 || []).forEach((id) => {
        const child = group.l2Map[id];
        if (child) {
          count += 1;
          texts.push(isEn ? child.text : child.textCn);
        }
      });
    });

    return {
      selectedTotal: count,
      allTotal: all,
      selectedTexts: texts,
      isAll: all > 0 && count === all,
    };
  }, [value, options, isEn]);

  let display: React.ReactNode;
  if (selectedTotal === 0) {
    display = intl({ id: 'None' });
  } else if (isAll) {
    display = intl({ id: 'All' });
  } else if (selectedTexts.length <= maxShow) {
    const list = selectedTexts.slice(0, maxShow);
    display = (
      <>
        {list.map((item, idx) => (
          <Fragment key={idx}>
            {item}
            {idx < list.length - 1 ? ',' : ''}
          </Fragment>
        ))}
      </>
    );
  } else {
    display = selectedTotal;
  }

  return (
    <div className="dbom-dropdow-button">
      <label className="dbom-dropdow-button-label">
        {isEn ? title : titleCn}
      </label>
      <div className="dbom-dropdow-button-value">{display}</div>
    </div>
  );
}
